import * as React from 'react';
import Paper from '@mui/material/Paper';
import Chatbot from './Chatbot';
import FaqMenu from './FaqMenu';
import MessageBubble from './MessageBubble';
import { CHATBOT_Z_INDEX, PANEL_BOTTOM, PANEL_RIGHT, VIEWPORT_MARGIN } from './chatbotLayout';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import AddCommentIcon from '@mui/icons-material/AddComment';
import EmailIcon from '@mui/icons-material/Email';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Slide from '@mui/material/Slide';
import Button from '@mui/material/Button';

import styles from './ScrollDialog.module.css';

// Scale with the viewport, but stay readable on small screens and avoid
// stretching into a full-page panel on very large ones.
const panelSize = {
    width: 'clamp(300px, 26vw, 450px)',
    height: 'clamp(400px, 62vh, 720px)',
    maxWidth: `calc(100vw - ${PANEL_RIGHT + VIEWPORT_MARGIN}px)`,
    maxHeight: `calc(100vh - ${PANEL_BOTTOM + VIEWPORT_MARGIN}px)`,
};

// Lower bounds for a manual resize, so the panel stays usable when shrunk.
const MIN_WIDTH = 300;
const MIN_HEIGHT = 400;

export default function ScrollDialog() {
    const [spin, setSpin] = React.useState(true);
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
        const timer = setTimeout(() => setSpin(false), 800);
        return () => clearTimeout(timer);
    }, []);

    React.useEffect(() => {
        if (open) return;
        const interval = setInterval(() => {
            setSpin(true);
            setTimeout(() => setSpin(false), 800);
        }, 60000);
        return () => clearInterval(interval);
    }, [open]);
    const [view, setView] = React.useState('faq'); // 'faq' | 'faqAnswer' | 'chat'
    const [faqAnswer, setFaqAnswer] = React.useState(null);

    const [messages, setMessages] = React.useState([
        { id: Date.now(), sender: 'bot', text: "Hello! I'm Pixel, the WARA-Ops dataportal assistant! How can I help you?" }
    ]);

    const [chatReset, setChatReset] = React.useState(0);

    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleNewChat = () => {
        setMessages([
            { id: Date.now(), sender: 'bot', text: "Hello! I'm Pixel, the WARA-Ops dataportal assistant! How can I help you?" }
        ]);
        setChatReset(prev => prev + 1);
        setView('faq');
        handleMenuClose();
    };

    const handleFaqSelect = (question, answer) => {
        setFaqAnswer({ question, answer });
        setView('faqAnswer');
    };

    const handleFaqSkip = () => {
        setView('chat');
    };

    const handleOpenChat = () => {
        setMessages(prev => [
            ...prev,
            { id: Date.now() + 1, sender: 'user', text: faqAnswer.question },
            { id: Date.now() + 2, sender: 'bot', text: faqAnswer.answer },
        ]);
        setView('chat');
    };

    const handleSendEmail = () => {
        const chatText = messages
            .map(msg => `${msg.sender === 'user' ? 'You' : 'Pixel'}: ${msg.text}`)
            .join('\n\n');
        const subject = encodeURIComponent('Chat conversation with Pixel');
        const body = encodeURIComponent(chatText);
        window.open(`mailto:?subject=${subject}&body=${body}`);
        handleMenuClose();
    };

    const handleToggle = () => {
        setOpen((prev) => !prev);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // User-set panel size (px). Null until the user drags the resize handle,
    // in which case the responsive default in `panelSize` applies instead.
    const [size, setSize] = React.useState(null);

    const handleResizeStart = (event) => {
        event.preventDefault();
        const point = event.touches ? event.touches[0] : event;
        const startX = point.clientX;
        const startY = point.clientY;
        // Panel grows toward the top-left, so dragging up/left enlarges it.
        const startWidth = size ? size.width : event.currentTarget.parentElement.offsetWidth;
        const startHeight = size ? size.height : event.currentTarget.parentElement.offsetHeight;

        const maxWidth = window.innerWidth - (PANEL_RIGHT + VIEWPORT_MARGIN);
        const maxHeight = window.innerHeight - (PANEL_BOTTOM + VIEWPORT_MARGIN);

        const handleMove = (moveEvent) => {
            const movePoint = moveEvent.touches ? moveEvent.touches[0] : moveEvent;
            const nextWidth = startWidth + (startX - movePoint.clientX);
            const nextHeight = startHeight + (startY - movePoint.clientY);
            setSize({
                width: Math.max(MIN_WIDTH, Math.min(nextWidth, maxWidth)),
                height: Math.max(MIN_HEIGHT, Math.min(nextHeight, maxHeight)),
            });
        };

        const handleEnd = () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleEnd);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleEnd);
        };

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleEnd);
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', handleEnd);
    };

    return (
        <React.Fragment>
            <Box sx={{ '& > :not(style)': { m: 1 } }}>
                <Fab
                    aria-label="SmartToyIcon"
                    onClick={handleToggle}
                    className={spin ? styles.spin : undefined}
                    sx={{
                        backgroundColor: '#fff',
                        color: '#6b7b8a',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                >
                    <SmartToyIcon />
                </Fab>
            </Box>
            <Slide direction="up" in={open} mountOnEnter unmountOnExit>
                <Paper
                    elevation={6}
                    sx={{
                        position: 'fixed',
                        bottom: PANEL_BOTTOM,
                        right: PANEL_RIGHT,
                        ...panelSize,
                        ...(size && { width: size.width, height: size.height }),
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        overflow: 'hidden',
                        zIndex: CHATBOT_Z_INDEX,
                    }}
                >
                    <Box
                        aria-label="resize"
                        title="Drag to resize"
                        onMouseDown={handleResizeStart}
                        onTouchStart={handleResizeStart}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: 24,
                            height: 24,
                            cursor: 'nwse-resize',
                            zIndex: 1400,
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            touchAction: 'none',
                            // Visual grip: an "L" of two lines in the corner.
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 6,
                                left: 6,
                                width: 9,
                                height: 9,
                                borderTop: '2px solid rgba(255, 255, 255, 0.85)',
                                borderLeft: '2px solid rgba(255, 255, 255, 0.85)',
                            },
                            '&:hover::before': {
                                borderColor: '#fff',
                            },
                        }}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            px: 2,
                            py: 1.5,
                            backgroundColor: '#6b7b8a',
                            color: '#fff',
                        }}
                    >
                        <Box
                            component="img"
                            src="/WARA_OPS_Logo_White.png"
                            alt="WARA Ops"
                            sx={{ height: 24 }}
                        />
                        <IconButton
                            aria-label="menu"
                            onClick={handleMenuOpen}
                            size="small"
                            sx={{ color: '#fff', position: 'absolute', right: 36 }}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchor}
                            open={menuOpen}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={handleNewChat}>
                                <ListItemIcon><AddCommentIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>New chat</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleSendEmail}>
                                <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Send to mail</ListItemText>
                            </MenuItem>
                        </Menu>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{ color: '#fff', position: 'absolute', right: 8 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {view === 'faq' && (
                            <FaqMenu onSelectQuestion={handleFaqSelect} onSkip={handleFaqSkip} />
                        )}
                        {view === 'faqAnswer' && faqAnswer && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <MessageBubble sender="user">{faqAnswer.question}</MessageBubble>
                                    <MessageBubble sender="bot">{faqAnswer.answer}</MessageBubble>
                                </Box>
                                <Box sx={{ display: 'flex', gap: '8px', padding: '10px 12px', boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.06)', backgroundColor: '#fff' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setView('faq')}
                                        sx={{ flex: 1, textTransform: 'none', borderColor: '#ddd', color: '#444', borderRadius: '12px', fontSize: '12px' }}
                                    >
                                        Ask another question
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleOpenChat}
                                        sx={{ flex: 1, textTransform: 'none', backgroundColor: '#6b7b8a', borderRadius: '12px', fontSize: '12px', '&:hover': { backgroundColor: '#5a6a78' } }}
                                    >
                                        Open chat
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        {view === 'chat' && (
                            <Chatbot
                                messages={messages}
                                setMessages={setMessages}
                                chatReset={chatReset}
                                onShowFaq={() => setView('faq')}
                            />
                        )}
                    </Box>
                </Paper>
            </Slide>
        </React.Fragment>
    );
}
